/**
 * ArtefactExportDialog Component
 *
 * Dialog for batch exporting multiple artefacts to various formats (PowerPoint, Word, PDF).
 * Features drag-and-drop reordering for customizing export order.
 *
 * Note: The @hello-pangea/dnd library may show a nested scroll container warning in development.
 * This is a known limitation when using drag-and-drop inside modals with scrollable content.
 * The warning is development-only and does not affect functionality or production builds.
 * See: https://github.com/atlassian/react-beautiful-dnd/issues/131
 */

import type { RuntimeBrand as Brand } from "@webuild-ai/react-ui/types";

import React, { useState, useEffect } from "react";
import { Card, CardContent, Chip } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { DragDropContext, Droppable, Draggable, DropResult } from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";
import { useUser, useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import {
  ModalOverlay,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalActionButton,
  ModalCancelButton,
} from "@/components/UI/ModalOverlay";
import { Artefact, ARTEFACT_TYPE_META } from "@/types/artefacts";
import {
  BatchExportFormat,
  batchExportToPDFBlob,
  batchExportToWordBlob,
} from "@/utils/artefactExport";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { DEFAULT_FONT, extractPrimaryFont } from "@/utils/fontUtils";
import { logger } from "@/telemetry";
import { verifyBrandDefaultExists } from "@/utils/templateEngine";
import { useTemplateStore } from "@/stores/TemplateStore";

import { WordPreviewModal } from "./WordPreviewModal";
import { PDFPreviewModal } from "./PDFPreviewModal";

export interface ArtefactExportDialogProps {
  isOpen: boolean;
  onClose?: () => void;
  onOpenChange?: (open: boolean) => void;
  artefacts: Artefact[];
  onExport: (
    artefacts: Artefact[],
    format: BatchExportFormat,
    title?: string,
    useSeparateWorksheets?: boolean,
    fontFamily?: string,
    codeFontFamily?: string,
  ) => Promise<void>;
  brand: Brand;
  theme: "light" | "dark";
}

const getExportFormats = (t: (key: string) => string) => [
  {
    key: "word",
    label: t("Word Document"),
    shortLabel: t("Word"),
    icon: "vscode-icons:file-type-word",
    extension: ".docx",
    description: t("Full document with formatted content"),
  },
  {
    key: "pdf",
    label: t("PDF Document"),
    shortLabel: t("PDF"),
    icon: "vscode-icons:file-type-pdf2",
    extension: ".pdf",
    description: t("Portable document with text selection"),
  },
  {
    key: "powerpoint",
    label: t("PowerPoint Presentation"),
    shortLabel: t("PowerPoint"),
    icon: "vscode-icons:file-type-powerpoint",
    extension: ".pptx",
    description: t("Slides with headings as section dividers"),
  },
  {
    key: "excel",
    label: t("Excel Spreadsheet"),
    shortLabel: t("Excel"),
    icon: "vscode-icons:file-type-excel",
    extension: ".xlsx",
    description: t("Spreadsheet with tables and formatted data"),
  },
];

export const ArtefactExportDialog: React.FC<ArtefactExportDialogProps> = ({
  isOpen,
  onClose: onCloseProp,
  onOpenChange,
  artefacts: initialArtefacts,
  onExport,
  brand: _brand,
  theme: _theme,
}) => {
  const handleCloseDialog = () => {
    onCloseProp?.();
    onOpenChange?.(false);
  };
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { userProfile } = useUser();
  const { user } = useAuth();
  const { exportFontFamily } = useUIPreferences();

  // Resolve the author for template placeholders (mirrors ArtefactsPanel)
  const getAuthorName = (): string => userProfile?.name || user?.name || brand.name;

  /**
   * Get the effective font family for Word exports based on user preference
   * "brand" = use brand font, otherwise use the explicitly selected font.
   * Uses extractPrimaryFont() to handle CSS font-family strings like "Poppins,sans-serif"
   * since the docx library expects a single font name, not a font stack.
   */
  const getExportFontFamily = (): string => {
    if (exportFontFamily === "brand") {
      return extractPrimaryFont(brand.fontFamily || DEFAULT_FONT);
    }

    return extractPrimaryFont(exportFontFamily);
  };
  const [format, setFormat] = useState<BatchExportFormat>("word");
  const [orderedArtefacts, setOrderedArtefacts] = useState<Artefact[]>(initialArtefacts);
  const [isExporting, setIsExporting] = useState(false);
  const [customTitle, setCustomTitle] = useState("");
  const [useSeparateWorksheets, setUseSeparateWorksheets] = useState(false);

  // Template toggle — initialise from global store, but allow per-export override
  const { useTemplateForExports: globalTemplateEnabled, getTemplateMetadata } = useTemplateStore();
  const [useTemplateForExport, setUseTemplateForExport] = useState(globalTemplateEnabled);

  // Sync with global store when dialog opens
  useEffect(() => {
    if (isOpen) {
      setUseTemplateForExport(globalTemplateEnabled);
    }
  }, [isOpen, globalTemplateEnabled]);

  // Verified template names — only show if file actually exists or user uploaded one
  const [verifiedTemplateNames, setVerifiedTemplateNames] = useState<Record<string, string | null>>(
    {},
  );

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const formats: Array<{ key: string; format: "word" | "pptx" | "xlsx" }> = [
      { key: "word", format: "word" },
      { key: "powerpoint", format: "pptx" },
      { key: "excel", format: "xlsx" },
    ];

    Promise.all(
      formats.map(async ({ key, format: fmt }) => {
        const userMeta = getTemplateMetadata(fmt);

        if (userMeta) return { key, name: userMeta.name };
        const brandName = await verifyBrandDefaultExists(fmt, brand.name);

        return { key, name: brandName };
      }),
    ).then((results) => {
      if (cancelled) return;
      const names: Record<string, string | null> = {};

      for (const { key, name } of results) {
        names[key] = name;
      }
      setVerifiedTemplateNames(names);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, brand.name, getTemplateMetadata]);

  // PDF Preview state
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [isGeneratingPdfPreview, setIsGeneratingPdfPreview] = useState(false);
  const [pdfPreviewData, setPdfPreviewData] = useState<ArrayBuffer | null>(null);

  // Word Preview state
  const [isWordPreviewOpen, setIsWordPreviewOpen] = useState(false);
  const [isGeneratingWordPreview, setIsGeneratingWordPreview] = useState(false);
  const [wordPreviewData, setWordPreviewData] = useState<ArrayBuffer | null>(null);

  // Suppress @hello-pangea/dnd nested scroll container warning in development.
  // This is a known cosmetic warning from the DnD library when used inside modals.
  useEffect(() => {
    if (!isOpen || !import.meta.env.DEV) return;

    const originalWarn = console.warn;

    console.warn = (...args: unknown[]) => {
      if (
        typeof args[0] === "string" &&
        args[0].includes("@hello-pangea/dnd") &&
        args[0].includes("nested scroll container")
      ) {
        return;
      }
      originalWarn.apply(console, args);
    };

    return () => {
      console.warn = originalWarn;
    };
  }, [isOpen]);

  // Reset state when dialog opens with new artefacts
  useEffect(() => {
    if (isOpen) {
      setOrderedArtefacts(initialArtefacts);
      setFormat("word");
      setIsExporting(false);
      setCustomTitle("");
      setUseSeparateWorksheets(false);
      // Reset PDF preview state
      setPdfPreviewData(null);
      setIsPdfPreviewOpen(false);
      // Reset Word preview state
      setWordPreviewData(null);
      setIsWordPreviewOpen(false);
    }
  }, [isOpen, initialArtefacts]);

  // Handle drag-and-drop reordering
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(orderedArtefacts);
    const [reorderedItem] = items.splice(result.source.index, 1);

    items.splice(result.destination.index, 0, reorderedItem);
    setOrderedArtefacts(items);
  };

  // Handle removing an artefact from the export list
  const handleRemoveArtefact = (artefactId: string) => {
    setOrderedArtefacts((prev) => prev.filter((a) => a.id !== artefactId));
  };

  /**
   * Run an async callback with the local template toggle applied to the global store.
   * Restores the original value afterwards regardless of success/failure.
   */
  const withTemplateOverride = async <T,>(fn: () => Promise<T>): Promise<T> => {
    const previousGlobal = useTemplateStore.getState().useTemplateForExports;
    const needsOverride = useTemplateForExport !== previousGlobal;

    if (needsOverride) {
      useTemplateStore.setState({ useTemplateForExports: useTemplateForExport });
    }

    try {
      return await fn();
    } finally {
      if (needsOverride) {
        useTemplateStore.setState({ useTemplateForExports: previousGlobal });
      }
    }
  };

  // Handle export button click
  const handleExport = async () => {
    setIsExporting(true);

    try {
      await withTemplateOverride(() =>
        onExport(
          orderedArtefacts,
          format,
          customTitle || undefined,
          format === "excel" ? useSeparateWorksheets : undefined,
          getExportFontFamily(),
          brand.codeFontFamily,
        ),
      );
      handleCloseDialog();
    } catch (error) {
      logger.error("Export failed", error);
      // Error toast is handled by parent
    } finally {
      setIsExporting(false);
    }
  };

  // Handle PDF preview
  const handlePdfPreview = async () => {
    setIsGeneratingPdfPreview(true);
    try {
      const pdfData = await batchExportToPDFBlob(orderedArtefacts, {
        title: customTitle || undefined,
        author: getAuthorName(),
      });

      setPdfPreviewData(pdfData);
      setIsPdfPreviewOpen(true);
    } catch (error) {
      logger.error("PDF preview generation failed", error);
    } finally {
      setIsGeneratingPdfPreview(false);
    }
  };

  // Handle Word preview (respects local template toggle)
  const handleWordPreview = async () => {
    setIsGeneratingWordPreview(true);
    try {
      const wordData = await withTemplateOverride(() =>
        batchExportToWordBlob(orderedArtefacts, {
          title: customTitle || undefined,
          author: getAuthorName(),
          fontFamily: getExportFontFamily(),
          codeFontFamily: brand.codeFontFamily,
          brandName: brand.name,
        }),
      );

      setWordPreviewData(wordData);
      setIsWordPreviewOpen(true);
    } catch (error) {
      logger.error("Word preview generation failed", error);
    } finally {
      setIsGeneratingWordPreview(false);
    }
  };

  // Handle download from PDF preview modal
  const handleDownloadFromPdfPreview = () => {
    if (!pdfPreviewData) return;

    const blob = new Blob([pdfPreviewData], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").split("T");
    const filename = `batch_export_${timestamp[0]}_${timestamp[1].substring(0, 8)}.pdf`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setIsPdfPreviewOpen(false);
    handleCloseDialog();
  };

  // Handle download completion from Word preview modal (download already performed by modal)
  const handleDownloadFromWordPreview = () => {
    setIsWordPreviewOpen(false);
  };

  const EXPORT_FORMATS = getExportFormats(t);
  const selectedFormatInfo = EXPORT_FORMATS.find((f) => f.key === format);

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay isOpen={isOpen} size="2xl" onClose={handleCloseDialog}>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Icon icon="solar:export-bold" width={24} />
            <span>{t("Export Generated Content")}</span>
          </div>
          <p className="text-sm font-normal text-default-500">
            {t("Choose format and arrange content order")}
          </p>
        </ModalHeader>
        <ModalBody>
          {/* Document Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-default-700">{t("Document Title")}</label>
            <input
              autoFocus
              className="w-full text-sm border border-default-200 rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              placeholder={t("Enter document title (optional)")}
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
            />
          </div>

          {/* Format Selection */}
          <div className="space-y-2 mt-4">
            <label className="text-sm font-medium text-default-700">{t("Export Format")}</label>
            <select
              aria-label={t("Export format")}
              className="w-full text-sm border border-default-200 rounded-lg px-3 py-2 bg-background shadow-md"
              value={format}
              onChange={(e) => setFormat(e.target.value as BatchExportFormat)}
            >
              {EXPORT_FORMATS.map((fmt) => (
                <option key={fmt.key} value={fmt.key}>
                  {fmt.label} ({fmt.extension})
                </option>
              ))}
            </select>
            {selectedFormatInfo?.description && (
              <p className="text-xs text-default-500 mt-1">{selectedFormatInfo.description}</p>
            )}
          </div>

          {/* Excel-specific options */}
          {format === "excel" && (
            <div
              className={`mt-4 p-4 rounded-lg border-2 transition-[background-color,border-color,box-shadow] duration-200 ${
                useSeparateWorksheets
                  ? "bg-brand-primary/5 border-brand-primary"
                  : "bg-default-100 dark:bg-default-50 border-transparent"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    aria-checked={useSeparateWorksheets}
                    aria-label={t("Export to separate worksheets")}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${useSeparateWorksheets ? "bg-primary" : "bg-default-300"}`}
                    role="switch"
                    type="button"
                    onClick={() => setUseSeparateWorksheets(!useSeparateWorksheets)}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${useSeparateWorksheets ? "translate-x-4.5" : "translate-x-0.5"}`}
                    />
                  </button>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {t("Export to separate worksheets")}
                    </span>
                    <span className="text-xs text-default-500">
                      {t("Create one worksheet per artefact instead of combining all")}
                    </span>
                  </div>
                </div>
                {useSeparateWorksheets && (
                  <Icon
                    className="text-brand-primary flex-shrink-0"
                    icon="solar:check-circle-bold"
                    width={20}
                  />
                )}
              </div>
            </div>
          )}

          {/* Template Toggle — only shown if a template file actually exists */}
          {verifiedTemplateNames[format] && (
            <div
              className={`flex items-center gap-2 mt-3 p-3 rounded-lg border transition-colors ${
                useTemplateForExport
                  ? "bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800"
                  : "bg-default-100 dark:bg-default-50 border-default-200 dark:border-default-700"
              }`}
            >
              <Icon
                className={`shrink-0 ${useTemplateForExport ? "text-primary" : "text-default-400"}`}
                icon="solar:document-bold"
                width={16}
              />
              <div className="flex-1 min-w-0">
                <span
                  className={`text-xs ${useTemplateForExport ? "text-primary-700 dark:text-primary-300" : "text-default-500"}`}
                >
                  {useTemplateForExport ? (
                    <>
                      {t("Using template")}: <strong>{verifiedTemplateNames[format]}</strong>
                    </>
                  ) : (
                    t("Template available but disabled for this export")
                  )}
                </span>
              </div>
              <button
                aria-checked={useTemplateForExport}
                aria-label={t("Use template for this export")}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${useTemplateForExport ? "bg-primary" : "bg-default-300"}`}
                role="switch"
                type="button"
                onClick={() => setUseTemplateForExport(!useTemplateForExport)}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${useTemplateForExport ? "translate-x-4.5" : "translate-x-0.5"}`}
                />
              </button>
            </div>
          )}

          {/* Artefacts Reordering */}
          <div className="space-y-2 mt-4">
            <label className="text-sm font-medium text-default-700">
              {t("Content Order ({{count}} items)", { count: orderedArtefacts.length })}
            </label>
            <p className="text-xs text-default-500">
              {t("Drag to reorder • Click X to remove from export")}
            </p>

            {orderedArtefacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Icon className="text-default-300 mb-2" icon="solar:inbox-line-bold" width={48} />
                <p className="text-sm text-default-500">{t("No items to export")}</p>
                <p className="text-xs text-default-400 mt-1">{t("All items have been removed")}</p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="artefacts">
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        className="space-y-0"
                        {...provided.droppableProps}
                      >
                        {orderedArtefacts.map((artefact, index) => {
                          const typeMeta = ARTEFACT_TYPE_META[artefact.type];

                          return (
                            <Draggable key={artefact.id} draggableId={artefact.id} index={index}>
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  className={snapshot.isDragging ? "opacity-70 shadow-lg" : ""}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                >
                                  <CardContent className="px-1 py-0">
                                    <div className="flex items-center gap-2">
                                      <Icon
                                        className="text-default-400 cursor-grab active:cursor-grabbing flex-shrink-0"
                                        icon="solar:hamburger-menu-line-duotone"
                                        width={20}
                                      />
                                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-default-100 text-xs font-medium text-default-500 flex-shrink-0">
                                        {index + 1}
                                      </div>
                                      <div
                                        className={`p-2 rounded-lg bg-default-100 ${typeMeta.color} flex-shrink-0`}
                                      >
                                        <Icon height={16} icon={typeMeta.icon} width={16} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-default-800 truncate">
                                            {artefact.title}
                                          </span>
                                          <Chip
                                            className="flex-shrink-0"
                                            size="sm"
                                            variant="primary"
                                          >
                                            {typeMeta.label}
                                          </Chip>
                                        </div>
                                      </div>
                                      <button
                                        aria-label={t("Remove {{title}}", {
                                          title: artefact.title,
                                        })}
                                        className="flex-shrink-0 text-danger hover:text-danger-600 transition-colors"
                                        type="button"
                                        onClick={() => handleRemoveArtefact(artefact.id)}
                                      >
                                        <Icon icon="solar:close-circle-bold" width={22} />
                                      </button>
                                    </div>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          {/*
            Three levels of emphasis, not three identical pills. Cancel is the
            white bordered pill, Preview is the neutral middle tier and Export
            alone carries the tenant fill.

            These were four hand-rolled `<button>` elements until the footer
            components landed. The reason they existed is worth recording, since
            it looks like gratuitous divergence: `heroui-v2-compat.css` carries an
            unlayered `border: none` on `button[data-rac]`, which beat both
            HeroUI's `.button--outline` border and any Tailwind `border-*`
            utility, so a bordered pill was genuinely unreachable through
            HeroUI's Button. A native `<button>` has no `data-rac` and so escaped
            it. That rule is now scoped with `:not(.button--outline)`, which is
            what makes ModalCancelButton possible here and everywhere else.

            The recessive pair still takes the neutral `default` scale rather
            than a brand outline. `--brand-primary` is a light-mode-safe value
            for some tenants only, LSEG's `#001EFF` being 8:1 on white but far
            less against its own dark surface, whereas the `default` stops are
            redefined for dark in the v2-compat layer.
          */}
          <ModalCancelButton onPress={() => handleCloseDialog()}>{t("Cancel")}</ModalCancelButton>
          {format === "pdf" && (
            <ModalActionButton
              isDisabled={orderedArtefacts.length === 0 || isGeneratingPdfPreview}
              tone="neutral"
              onPress={handlePdfPreview}
            >
              {!isGeneratingPdfPreview && <Icon icon="solar:eye-bold" width={16} />}
              {isGeneratingPdfPreview ? t("Generating...") : t("Preview")}
            </ModalActionButton>
          )}
          {format === "word" && (
            <ModalActionButton
              isDisabled={orderedArtefacts.length === 0 || isGeneratingWordPreview}
              tone="neutral"
              onPress={handleWordPreview}
            >
              {!isGeneratingWordPreview && <Icon icon="solar:eye-bold" width={16} />}
              {isGeneratingWordPreview ? t("Generating...") : t("Preview")}
            </ModalActionButton>
          )}
          <ModalActionButton
            isDisabled={orderedArtefacts.length === 0 || isExporting}
            onPress={handleExport}
          >
            {!isExporting && <Icon icon="solar:export-bold" width={16} />}
            {isExporting
              ? t("Exporting...")
              : t("Export to {{format}}", {
                  format: selectedFormatInfo?.shortLabel || t("Document"),
                })}
          </ModalActionButton>
        </ModalFooter>
      </ModalOverlay>

      {/* PDF Preview Modal */}
      <PDFPreviewModal
        isOpen={isPdfPreviewOpen}
        pdfData={pdfPreviewData}
        title={customTitle || ""}
        onClose={() => setIsPdfPreviewOpen(false)}
        onDownload={handleDownloadFromPdfPreview}
        onOpenChange={(open: boolean) => {
          if (!open) setIsPdfPreviewOpen(false);
        }}
      />

      {/* Word Preview Modal */}
      <WordPreviewModal
        isLoading={isGeneratingWordPreview}
        isOpen={isWordPreviewOpen}
        title={customTitle || ""}
        wordData={wordPreviewData}
        onClose={() => setIsWordPreviewOpen(false)}
        onDownload={handleDownloadFromWordPreview}
        onOpenChange={(open: boolean) => {
          if (!open) setIsWordPreviewOpen(false);
        }}
      />
    </>
  );
};
